import { EventEmitter, Injectable, OnDestroy, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  LicenseStatus,
  SubscriptionLicensesResponseDTO,
} from '../../../../domains/payment/subscription/models/SubscriptionDTO';
import { SubscriptionService } from '../../../../domains/payment/subscription/services/subscription.service';
import { UserService } from '../../../../domains/organization/user/services/user.service';
import { UserResponseDTO } from '../../../../domains/organization/user/models/UserDTO';
import { LanguageService } from '../../../../core/services/language.service';
import { AnalyticsService } from '../../../../core/services/analytics/analytics.service';
import { SepTableSelectionService } from '../../../../shared/components/sep-table/services/sep-table-selection.service';
import { UserUIModel } from '../../../../domains/organization/user/models/UserUIModel';
import { StudentEligibleForEnrollmentBaseUIModel } from '../../../../domains/academy/base/EnrolledStudentBaseUIModel';
import { CurrentUserState } from '../../../../core/store/current-user/current-user.state';
import { Store } from '@ngxs/store';

@Injectable({
  providedIn: 'root',
})
export abstract class EnrollmentContainerBase implements OnDestroy {
  protected readonly languageService = inject(LanguageService);
  protected readonly subscriptionService = inject(SubscriptionService);
  protected readonly userService = inject(UserService);
  protected readonly analyticsService = inject(AnalyticsService);
  protected readonly tableSelectionService;
  protected readonly store = inject(Store);

  isLoading = false;
  canEnroll = false;
  pendingLicenseAssignments = 0;
  studentsEligibleForEnrollment:
    | StudentEligibleForEnrollmentBaseUIModel[]
    | undefined = undefined;
  selectedStudents: StudentEligibleForEnrollmentBaseUIModel[];
  filteredStudents: StudentEligibleForEnrollmentBaseUIModel[] | undefined;
  searchTerm = '';
  availableLicenses$: Observable<number>;
  abstract abortButtonClickEvent: EventEmitter<void>;
  abstract studentSuccessfullyEnrolledEvent: EventEmitter<void>;
  abstract userCreationButtonClickEvent: EventEmitter<void>;

  protected destroy$: Subject<void> = new Subject();

  protected constructor() {
    this.tableSelectionService = inject<SepTableSelectionService<UserUIModel>>(
      SepTableSelectionService,
    );
  }
  abstract getStudentsEligibleForEnrollment(): void;
  abstract enrollStudents(): void;

  observeTableSelection() {
    this.tableSelectionService.selection$
      .pipe(takeUntil(this.destroy$))
      .subscribe((selection) => {
        this.selectedStudents = selection.selected;
        this.checkLicenseEligibilityForEnrollment()
          .pipe(takeUntil(this.destroy$))
          .subscribe((canEnroll) => {
            this.canEnroll = canEnroll;
          });
        this.pendingLicenseAssignments =
          this.countSelectedStudentsWithoutLicenseAssigned();
        this.checkLicenseEligibilityForEnrollment();
      });
  }

  checkLicenseEligibilityForEnrollment(): Observable<boolean> {
    return this.availableLicenses$.pipe(
      map(
        (availableLicenses) =>
          availableLicenses -
          this.countSelectedStudentsWithoutLicenseAssigned(),
      ),
      map((licenseDiff) => licenseDiff >= 0),
    );
  }

  countSelectedStudentsWithoutLicenseAssigned(): number {
    return this.selectedStudents.reduce((count, student) => {
      if (!student.license?.status) {
        count += 1;
      }
      return count;
    }, 0);
  }

  /*we compare enrolled students to all users in the organization.
      The diff reflects students that are eligible to be enrolled in the course.*/
  getEligibleStudents(
    enrolledStudents: Array<{ id: string }>,
  ): Observable<void> {
    return this.userService.getActiveUsers().pipe(
      map((responseDTO) => {
        const usersEligibleForEnrollment = responseDTO.users.filter(
          (user) =>
            !enrolledStudents.some(
              (enrolledStudent) => enrolledStudent.id === user.id,
            ),
        );
        this.studentsEligibleForEnrollment = this.transformUsersToStudents(
          usersEligibleForEnrollment,
        );
      }),
    );
  }

  getAvailableLicenses() {
    this.availableLicenses$ = this.subscriptionService.getLicenses().pipe(
      takeUntil(this.destroy$),
      map(
        (response: SubscriptionLicensesResponseDTO) =>
          response.licenses.filter(
            (license) => license.status === LicenseStatus.AVAILABLE,
          ).length,
      ),
    );
  }

  getEnrollmentDescription() {
    const numberOfLicenses = this.countSelectedStudentsWithoutLicenseAssigned();
    if (numberOfLicenses === 0) {
      return this.languageService.translateOnRuntime(
        'portal.courses.enrollment.licenses.assignmentExplanationZero',
      );
    } else if (numberOfLicenses === 1) {
      return this.languageService.translateOnRuntime(
        'portal.courses.enrollment.licenses.assignmentExplanationSingular',
      );
    } else {
      return this.languageService.translateOnRuntime(
        'portal.courses.enrollment.licenses.assignmentExplanationPlural',
        {
          numberOfLicenses,
        },
      );
    }
  }

  handleUserCreationButtonClick(): void {
    this.analyticsService.track('create_employees_clicked');
    this.userCreationButtonClickEvent.emit();
  }

  handleAbortButtonClick(): void {
    this.abortButtonClickEvent.emit();
  }

  protected transformUsersToStudents(
    users: UserResponseDTO[],
  ): StudentEligibleForEnrollmentBaseUIModel[] {
    return users.map((user) => {
      const username = `${user.firstName} ${user.lastName}`;
      const { email, id, licenses, syncStatus, role } = user;
      return {
        id,
        username,
        email,
        syncStatus,
        license: licenses.length ? licenses[0] : null,
        role,
      };
    });
  }

  onSearchTermChanged(searchTerm: string): void {
    this.searchTerm = searchTerm.toLowerCase().trim();
    this.applySearchFilter();
  }

  protected sortStudents(
    students: StudentEligibleForEnrollmentBaseUIModel[],
  ): StudentEligibleForEnrollmentBaseUIModel[] {
    const userDetails = this.store.selectSnapshot(CurrentUserState.userDetails);
    const currentUserEmail = userDetails.email?.toLowerCase() ?? undefined;

    return students.sort((a, b) => {
      const aIsCurrentUser =
        currentUserEmail && a.email.toLowerCase() === currentUserEmail;
      const bIsCurrentUser =
        currentUserEmail && b.email.toLowerCase() === currentUserEmail;
      const aIsAdmin = a.role === 'admin';
      const bIsAdmin = b.role === 'admin';

      // Current user comes first
      if (aIsCurrentUser) {
        return -1;
      }
      if (bIsCurrentUser) {
        return 1;
      }

      // Then admins, sorted alphabetically
      if (aIsAdmin && !bIsAdmin) {
        return -1;
      }
      if (!aIsAdmin && bIsAdmin) {
        return 1;
      }

      // Both admin or both non-admin: sort alphabetically
      return a.username.localeCompare(b.username);
    });
  }

  private applySearchFilter(): void {
    if (!this.studentsEligibleForEnrollment) {
      this.filteredStudents = undefined;
      return;
    }

    if (!this.searchTerm) {
      this.filteredStudents = this.studentsEligibleForEnrollment;
      return;
    }

    // Filter students based on search term (name or email)
    const matchingStudents = this.studentsEligibleForEnrollment.filter(
      (student) =>
        student.username.toLowerCase().includes(this.searchTerm) ||
        student.email.toLowerCase().includes(this.searchTerm),
    );

    // Sort the filtered results using the existing sort logic
    this.filteredStudents = this.sortStudents(matchingStudents);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
